import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FC, useMemo } from 'react';
import { captureException } from '@sentry/react';
import {
  ActionButtons,
  Error,
  getErrorMessage,
  loadingToast,
  PageHeader,
  Spinner,
  SpinnerWrapper,
  TSelectItems,
  TToastType,
  updateToast,
} from '@sim-admin-frontends/ui-shared';
import {
  ScrapeTaskInput,
  useSaAddTaskMutation,
  useSaTaskByCodeQuery,
} from '@sim-admin-frontends/data-access-admin-be';
import {
  CategoryType,
  SaInstitutionDocument,
  SaInstitutionQuery,
  SaInstitutionQueryVariables,
  fetcher,
  useCategoriesQuery,
  useSaInstitutionsByQuery,
  useSaUpdateInstitutionMutation,
} from '@sim-admin-frontends/data-access';
import { filterCategories } from '@sim-admin-frontends/utils-shared';

import { useGenerateTaskActions } from '../../../hooks/actionButtons/useGenerateTaskActions';
import TaskEdit from './TaskEdit';
import { TTaskEditFormValues } from '../../../types/TTask';
import ROUTES from '../../../routing/routes';
import NotFound from '../../../routing/NotFound';
import { transformFormValues } from '../../../utils/tasksUtils';
import { useSearchCombinedInstitutions } from '../../../hooks/useSearchCombinedInstitutions';
import { transformInstitutions } from '../../../utils/institutionUtils';
import { useSearchTaskQuery } from '../../../hooks/useSearchTaskQuery';

const SHARED_TOAST_ID = 'tasksToast';

type TaskEditContainerProps = {
  code?: string;
};

const TaskEditContainer: FC<TaskEditContainerProps> = ({ code }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const pageTitle = code ? t('tasks.editPageTitle') : t('tasks.createPageTitle');
  const pageCaption = code ? t('tasks.editPageCaption') : t('tasks.createPageCaption');

  const { mutateAsync: addTask } = useSaAddTaskMutation();
  const { mutateAsync: updateInstitution } = useSaUpdateInstitutionMutation();
  const { data, isLoading, isError, refetch } = useSaTaskByCodeQuery(
    {
      code: code || '',
    },
    {
      enabled: !!code,
    },
  );

  const {
    isLoading: institutionLoading,
    error: institutionError,
    data: institutionForTaskCode,
  } = useSaInstitutionsByQuery(
    {
      filter: {
        taskCode: code || '',
      },
    },
    {
      enabled: true,
    },
  );

  const institution = institutionForTaskCode?.institutionsBy.institutions[0];

  const {
    institutions,
    isLoadingInstitutions,
    isFetchingInstitutions,
    isErrorInsitutions,
    fetchNextPageInstitutions,
    hasNextPageInstitutions,
    onInstitutionsSearch,
  } = useSearchCombinedInstitutions();

  const onAllInstitutionsMenuScrollToBottom = () => {
    if (hasNextPageInstitutions) {
      fetchNextPageInstitutions();
    }
  };

  const initialInstitution = useMemo(
    () =>
      institution
        ? {
            label: institution.name,
            value: institution.id,
          }
        : undefined,
    [institution],
  );

  const institutionOptions: TSelectItems = transformInstitutions(institutions ?? []);

  const {
    tasks,
    isLoadingTasks,
    isFetchingTasks,
    isErrorTasks,
    fetchNextPageTasks,
    hasNextPageTasks,
    onTaskSearch,
  } = useSearchTaskQuery();

  const onAllTasksMenuScrollToBottom = () => {
    if (hasNextPageTasks) {
      fetchNextPageTasks();
    }
  };

  const checkCodesOptions = tasks
    ? tasks.map((task) => ({ label: task.code, value: task.code }))
    : [];

  const {
    isLoading: areAnnouncementCategoriesLoading,
    error: announcementCategoriesError,
    data: announcementCategoriesOfficial,
  } = useCategoriesQuery({
    filter: {
      categoryType: CategoryType.Announcement,
    },
  });

  const {
    isLoading: areEventCategoriesLoading,
    error: eventCategoriesError,
    data: eventCategoriesOfficial,
  } = useCategoriesQuery({
    filter: {
      categoryType: CategoryType.Event,
    },
  });

  const {
    isLoading: areAnnouncementVisitorCategoriesLoading,
    error: announcementVisitorCategoriesError,
    data: announcementVisitorCategoriesOfficial,
  } = useCategoriesQuery({
    filter: {
      categoryType: CategoryType.Announcement,
      visitorMode: true,
    },
  });

  const {
    isLoading: areEventVisitorCategoriesLoading,
    error: eventVisitorCategoriesError,
    data: eventVisitorCategoriesOfficial,
  } = useCategoriesQuery({
    filter: {
      categoryType: CategoryType.Event,
      visitorMode: true,
    },
  });

  const announcementCategories = useMemo(
    () =>
      filterCategories(announcementCategoriesOfficial?.categoriesBy.categories || [], {
        filterLegacy: true,
      }),
    [announcementCategoriesOfficial],
  );

  const eventCategories = useMemo(
    () =>
      filterCategories(eventCategoriesOfficial?.categoriesBy.categories || [], {
        filterLegacy: true,
      }),
    [eventCategoriesOfficial],
  );

  const announcementVisitorCategories = useMemo(
    () =>
      filterCategories(announcementVisitorCategoriesOfficial?.categoriesBy.categories || [], {
        filterLegacy: true,
      }),
    [announcementVisitorCategoriesOfficial],
  );

  const eventVisitorCategories = useMemo(
    () =>
      filterCategories(eventVisitorCategoriesOfficial?.categoriesBy.categories || [], {
        filterLegacy: true,
      }),
    [eventVisitorCategoriesOfficial],
  );

  const onSubmit = async (values: TTaskEditFormValues) => {
    try {
      loadingToast(t('tasks.createToast.loading'), {
        toastId: SHARED_TOAST_ID,
      });
      const mutationVariables: ScrapeTaskInput = transformFormValues(values);
      await addTask({
        task: mutationVariables,
      });
      const institutionChanged = institution?.id !== values.institution?.id;
      if (institutionChanged && institution) {
        const newCodes = institution?.taskCodes?.filter((taskCode) => taskCode !== code);
        await updateInstitution({
          id: institution.id,
          institution: {
            taskCodes: newCodes,
            name: institution.name,
            categoryUuid: institution.category.id,
            places:
              institution.places?.map((place) => ({
                placeId: place.id,
                role: institution.inPlaces ? institution.inPlaces[0].role : null,
              })) || [],
          },
        });
      }

      if (values.institution && code) {
        let res = null;
        try {
          const fetch = await fetcher<SaInstitutionQuery, SaInstitutionQueryVariables>(
            SaInstitutionDocument,
            { id: values.institution.id },
          );
          res = await fetch();
        } catch (err) {
          updateToast(SHARED_TOAST_ID, getErrorMessage(err), TToastType.ERROR);
          captureException(err);
        }
        if (!res || !res.institution) {
          return;
        }
        const newInstitution = res.institution;
        if (newInstitution) {
          const newCodes = newInstitution?.taskCodes ? [...newInstitution.taskCodes, code] : [code];
          await updateInstitution({
            id: values.institution.id,
            institution: {
              taskCodes: newCodes,
              name: newInstitution.name,
              categoryUuid: newInstitution.category.id,
              places:
                newInstitution.places?.map((place) => ({
                  placeId: place.id,
                  role: newInstitution.inPlaces ? newInstitution.inPlaces[0].role : null,
                })) || [],
            },
          });
        }
      }
      updateToast(
        SHARED_TOAST_ID,
        code ? t('tasks.editToast.success') : t('tasks.createToast.success'),
        TToastType.SUCCESS,
      );
      history.push(ROUTES.tasks.path);
    } catch (err) {
      updateToast(SHARED_TOAST_ID, getErrorMessage(err), TToastType.ERROR);
      captureException(err);
    }
  };

  const { detailActions, renderModal } = useGenerateTaskActions({
    code: code || '',
    refetch,
    isEditPage: true,
    scraperType: data?.scrapeTaskByCode?.scraperType,
    websiteTemplate: data?.scrapeTaskByCode?.websiteTemplate,
    dataType: data?.scrapeTaskByCode?.dataType,
  });

  if (
    isLoading ||
    areAnnouncementCategoriesLoading ||
    areEventCategoriesLoading ||
    areAnnouncementVisitorCategoriesLoading ||
    areEventVisitorCategoriesLoading ||
    institutionLoading ||
    isLoadingInstitutions ||
    isLoadingTasks
  ) {
    return (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    );
  }

  if (
    isError ||
    announcementCategoriesError ||
    eventCategoriesError ||
    announcementVisitorCategoriesError ||
    eventVisitorCategoriesError ||
    institutionError ||
    isErrorInsitutions ||
    isErrorTasks
  ) {
    return (
      <SpinnerWrapper>
        <Error caption={t('error.fetchingDataError')} onClick={refetch} />
      </SpinnerWrapper>
    );
  }

  if (code && !data?.scrapeTaskByCode) {
    return (
      <SpinnerWrapper>
        <NotFound />
      </SpinnerWrapper>
    );
  }

  return (
    <>
      <ActionButtons actionButtons={detailActions} />
      <PageHeader title={pageTitle} caption={pageCaption} />
      <TaskEdit
        onSubmit={onSubmit}
        task={data?.scrapeTaskByCode}
        announcementCategories={announcementCategories}
        eventCategories={eventCategories}
        announcementVisitorCategories={announcementVisitorCategories}
        eventVisitorCategories={eventVisitorCategories}
        institutions={institutionOptions}
        initialInstitution={initialInstitution}
        onAllInstitutionsMenuScrollToBottom={onAllInstitutionsMenuScrollToBottom}
        isFetchingInstitutions={isFetchingInstitutions}
        onInstitutionsSearch={onInstitutionsSearch}
        checkCodesOptions={checkCodesOptions}
        onAllTasksMenuScrollToBottom={onAllTasksMenuScrollToBottom}
        isFetchingTasks={isFetchingTasks}
        onTasksSearch={onTaskSearch}
      />
      {renderModal()}
    </>
  );
};

export default TaskEditContainer;
